/* eslint-disable react/no-danger */
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { trackEvent } from 'analytics/trackFunctions'
import { useAdvanceSuccess, useCommercialOffer } from 'store'
import { MeContext } from 'contexts/me'

// Components
import PageWrapper from 'components/PageWrapper'
import PrimaryButton from 'components/primaryButton'
import Card from 'components/Card'
import Tag from 'components/Tag'
import RoundedIcon from 'components/RoundedIcon'
import { FireMembership } from 'assets/icons'
import Disclaimer from 'components/Disclaimer'

// Utils
import { toLocaleDate } from 'utils/date-fns'
import { toLocaleCurrency } from 'utils/dinero'
import { hasActiveMembership } from 'utils'
import { CREDIT_TYPES, ATTEMPT_STATUS } from 'constants/credit'
import { SUBORIGIN_OPTIONS } from 'constants/membership'

// Api
import { sendOTP } from 'api/OTP'
import Badge from 'components/Badge'
import { createSubscriber } from 'api/memberships'
import { acceptAdvanceTerms } from 'api/productsTerms'
import { finishAdvance } from 'api/advances'
import { ADVANCE_TERMS_SLUG, getTerms } from 'api/term-conditions'
import { MEMBERSHIP_PRICE } from 'config'

const { SUBMITTED, SUBMITTED_WITH_DELAYS, REJECTED } = ATTEMPT_STATUS

const styles = {
  box: 'flex justify-between items-center py-2',
  key: 'text-xs font-medium',
  title:
    'flex justify-between items-center text-blue-900 text-xs font-bold py-2',
  value: 'text-xs font-bold flex gap-2 items-center',
  newValue: 'flex items-center text-[#A8A8A8] text-xs line-through'
}

const ConfirmRequestAdvance = () => {
  const [isLoadingConfirm, setIsLoadingConfirm] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)
  const [termsID, setTermsID] = useState(null)

  const { me } = useContext(MeContext)
  const navigate = useNavigate()

  const {
    creditLine,
    isMembershipSelected,
    selectedOffer: {
      membership: selectedAdvance,
      noMembership: oldOfferSelectedAdvance,
      startDate,
      creditOptionId,
      advanceType
    }
  } = useCommercialOffer(
    ({ selectedOffer, isMembershipSelected, creditLine }) => ({
      selectedOffer,
      isMembershipSelected,
      creditLine
    })
  )
  const { updateAdvanceData, updateIsDelayed } = useAdvanceSuccess()

  const formatPercentage = (value) => `${(Number(value) * 100).toFixed()}%`
  const formatCurrency = (value) => `$ ${toLocaleCurrency(value)}`

  const dueDate = toLocaleDate(startDate)

  const adv = {
    'Tasa Nominal Anual (TNA)': {
      newOffer: formatPercentage(selectedAdvance.interest_rate),
      oldOffer: formatPercentage(oldOfferSelectedAdvance.interest_rate)
    },
    'Costo Financiero Total (CFT)': {
      newOffer: formatPercentage(selectedAdvance.tfc),
      oldOffer: formatPercentage(oldOfferSelectedAdvance.tfc)
    },
    'Total a devolver': {
      newOffer: formatCurrency(selectedAdvance.repayable_amount),
      oldOffer: formatCurrency(oldOfferSelectedAdvance.repayable_amount)
    }
  }

  useEffect(() => {
    if (advanceType === CREDIT_TYPES.ZERO_RATE) {
      trackEvent({
        eventName:
          'bille_pantalla_adelantos_oferta_comercial_tasa_0_confirmacion'
      })
    } else {
      trackEvent({
        eventName: 'bille_adelantos_pantalla_confirmación'
      })
    }

    getTerms(ADVANCE_TERMS_SLUG).then((res) => {
      setTermsID(res?.identifier)
    })
  }, [])

  const handleConfirm = () => {
    setIsLoadingConfirm(true)

    if (advanceType === CREDIT_TYPES.ZERO_RATE) {
      trackEvent({
        eventName: 'bille_adelantos_oferta_comercial_tasa_0_confirmar'
      })
    } else {
      trackEvent({
        eventName: 'bille_adelantos_confirmación_confirmar'
      })
    }

    sendOTP().then(({ success, statusCode }) => {
      if (statusCode === 417) {
        // Chequeo de OTP si está activado o no
        checkMembershipAndAdvanceRequest()
        return
      }

      if (!success) {
        navigate('/pedir-adelanto/error-otp')
        return
      }

      return navigate('/pedir-adelanto/validar', { replace: true })
    })
  }

  // TODO: refactorizar estas funciones
  const checkMembershipAndAdvanceRequest = () => {
    if (isRequesting) return

    setIsRequesting(true)

    const eventProps = {
      monto_solicitado: selectedAdvance.delivered_amount,
      monto_linea_credito: creditLine.maxAmount,
      vencimiento_de_la_cuota: selectedAdvance.due_date,
      fecha_de_otorgamiento: selectedAdvance.start_date,
      monto_a_devolver: selectedAdvance.repayable_amount,
      cantidad_adelantos_previos: creditLine.advanceCounter
    }

    trackEvent({
      eventName: 'bille_adelantos_boton_oferta_confirmar',
      props: eventProps
    })

    if (isMembershipSelected && !hasActiveMembership(me?.subscriber_products)) {
      const subscriberData = {
        name: me.profile.name,
        surname: me.profile.surname,
        email: me.profile.email,
        identificationNumber: me.profile.identification_number,
        phone: me.profile.phone,
        userUuid: me.user.uuid,
        subOrigin:
          advanceType === CREDIT_TYPES.ZERO_RATE
            ? SUBORIGIN_OPTIONS.INTEREST_FREE_ADVANCE
            : SUBORIGIN_OPTIONS.CASH_ADVANCE
      }

      trackEvent({
        eventName:
          'bille_adelantos_oferta_solicitar_boton_suscripcion_wayniplus',
        props: eventProps
      })

      createSubscriber(subscriberData)
        .then((res) => {
          if (!res.success || res.error) {
            navigate('/error-adelanto')
          } else {
            setTimeout(() => {
              advanceRequest(eventProps)
            }, 2000)
          }
        })
        .catch(() => navigate('/error-adelanto'))
    } else {
      advanceRequest(eventProps)
    }
  }

  const advanceRequest = (eventProps) => {
    // delete possible cached data
    updateIsDelayed(false)
    // accept advance terms
    acceptAdvanceTerms({ identifier: termsID }).then(() =>
      finishAdvance({
        credit_option_id: creditOptionId,
        credit_option_plan_id: selectedAdvance.id,
        wallet_account: me.bank.internal_account[0].wallet_account,
        loan_type: advanceType
      })
        .then((res) => {
          if (res.success) {
            updateAdvanceData(
              selectedAdvance.delivered_amount,
              !hasActiveMembership(me?.subscriber_products) &&
                isMembershipSelected,
              advanceType
            )
            trackEvent({
              eventName: 'bille_adelantos_oferta_confirmar_exito',
              props: eventProps
            })

            if (res.data.result.status === REJECTED) {
              return navigate('/error-adelanto')
            }

            if (res.data.result.status === SUBMITTED_WITH_DELAYS) {
              updateIsDelayed(true)
            }

            navigate('/fin-adelanto', { replace: true })
          } else {
            trackEvent({
              eventName: 'bille_adelantos_oferta_confirmar_error',
              props: {
                ...eventProps,
                error: JSON.stringify(res)
              }
            })
            return navigate('/error-adelanto')
          }
        })
        .catch((error) => {
          if (advanceType === CREDIT_TYPES.ZERO_RATE) {
            trackEvent({
              eventName:
                'bille_pantalla_adelantos_oferta_comercial_tasa_0_error',
              props: {
                ...eventProps,
                error: JSON.stringify(error)
              }
            })
          } else {
            trackEvent({
              eventName: 'bille_adelantos_oferta_error',
              props: {
                ...eventProps,
                error: JSON.stringify(error)
              }
            })
          }
          navigate('/error-adelanto')
        })
        .finally(() => setIsRequesting(false))
    )
  }

  return (
    <PageWrapper viewSelected='/adelantos' title='Pedir adelanto' noTabBar>
      <section className='flex flex-col items-center mx-auto min-h-[75vh] w-full md:max-w-96'>
        <h2 className='font-bold text-center py-1 text-xl text-black mb-2'>
          Confirmá tu solicitud
        </h2>
        <Card className='bg-white text-black p-3 w-full !max-w-full'>
          <dl className='divide-y divide-gray-500 w-full pb-2'>
            <div className={styles.title}>
              <dt>Monto solicitado</dt>
              <dd>
                ${' '}
                {selectedAdvance.delivered_amount
                  ? toLocaleCurrency(selectedAdvance.delivered_amount)
                  : ''}
              </dd>
            </div>
            <div className={styles.box}>
              <dt className={styles.key}>
                Cuotas{advanceType === CREDIT_TYPES.ZERO_RATE ? '*' : null}
              </dt>
              <dd className={styles.value}>
                {advanceType === CREDIT_TYPES.ZERO_RATE && (
                  <Badge className='h-[18px]' variant='GRADIENT_GREEN'>
                    <span>Sin interés</span>
                  </Badge>
                )}
                {selectedAdvance.installments_quantity} de ${' '}
                {selectedAdvance.payment_amount
                  ? toLocaleCurrency(selectedAdvance.payment_amount)
                  : ''}
              </dd>
            </div>

            {Object.entries(adv).map(([key, { newOffer, oldOffer }]) => (
              <div key={uuid()} className={styles.box}>
                <dt className={styles.key}>{key}</dt>
                <dd className={styles.value}>
                  {isMembershipSelected ? (
                    <>
                      {advanceType !== CREDIT_TYPES.ZERO_RATE && (
                        <RoundedIcon
                          className='w-5 h-5 !min-h-[2px] !min-w-[2px]'
                          icon={
                            <FireMembership className='w-[0.625rem] h-[0.625rem]' />
                          }
                        />
                      )}{' '}
                      {key === 'Total a devolver' ? (
                        <span
                          className={
                            isMembershipSelected
                              ? styles.value
                              : styles.newValue
                          }
                        >
                          {newOffer}
                        </span>
                      ) : (
                        <span
                          className={
                            isMembershipSelected
                              ? styles.value
                              : styles.newValue
                          }
                        >
                          <span className={styles.newValue}>{oldOffer}</span>
                          {newOffer}
                        </span>
                      )}
                    </>
                  ) : (
                    <span>{oldOffer}</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
          <Tag className='mx-auto px-8 my-2 w-full'>
            Tu cuota vencerá el {dueDate} 🗓️
          </Tag>
          <p className='text-gray-900 font-medium text-2xs leading-[120%] mb-1'>
            Intentaremos debitar tu cuota 4 días hábiles antes de la fecha de
            vencimiento.
          </p>
          {!hasActiveMembership(me?.subscriber_products) &&
            isMembershipSelected && (
              <div className='flex flex-col justify-center items-center pt-3 text-xs font-medium'>
                <div className='flex'>
                  <RoundedIcon
                    className='w-5 h-5 !min-h-[2px] !min-w-[2px]'
                    icon={
                      <FireMembership className='w-[0.625rem] h-[0.625rem]' />
                    }
                  />
                  <p className='pl-2 text-black'>
                    Te estás suscribiendo a Wayni{' '}
                    <span className='text-[#0FD08B] pr-1'>+</span>
                  </p>
                </div>
                <p className='font-bold'>
                  $ {toLocaleCurrency(MEMBERSHIP_PRICE)} por mes
                </p>
              </div>
            )}
        </Card>
        {advanceType === CREDIT_TYPES.ZERO_RATE && (
          <p className='text-gray-900 text-2xs font-medium mt-3'>
            *Si bien los adelantos tasa 0 no tienen interés, en caso de haber
            demora, será informado al BCRA y se cobrarán intereses punitorios en
            base a la TNA original.
          </p>
        )}
        <Disclaimer
          className='mt-6'
          title='Información importante sobre adelantos'
          description='El dinero de tu adelanto solo podrá ser utilizado con tu tarjeta Wayni virtual o física.'
          borderVariant='green'
        />

        <PrimaryButton
          className='mt-auto md:mt-4'
          buttonText='Confirmar'
          primaryButtonFunction={handleConfirm}
          isLoading={isLoadingConfirm}
        />
      </section>
    </PageWrapper>
  )
}

export default ConfirmRequestAdvance
